import { EMPTY, Observable, of, throwError } from "rxjs";
import { Injectable } from "@angular/core";
import { UserService } from "./user.service";
import { DomSanitizer } from "@angular/platform-browser";
import { AuthenticatedHttp } from "./http.service";
import { HttpResponseHandlerService } from "./httpResponseHandler.service";
import { catchError, map, tap } from "rxjs/operators";
import { HttpParams } from "@angular/common/http";
import { AppConstants } from "../app.constants";
import { FileInfo } from "../models/documentManager/fileinfo.model";
import { EFileGroup } from "../models/documentManager/filegroup.enum";
import { ICategoryOrder, IOrderedCategory } from "../models/documentManager/category-order.model";

@Injectable()
export class FileService {
  constructor(private authHttp: AuthenticatedHttp, private userService: UserService, private sanitizer: DomSanitizer, private httpResponseHandlerService: HttpResponseHandlerService) {
  }

  /**
   * Get an image from target URL
   * @param {string} url
   * @returns {Observable<any>}
   */
  getImage(url: string): Observable<any> {
    return this.authHttp.getImage(url)
      .pipe(
        map((blob: any) => {
          if (!blob.type.startsWith("image/")) {
            throw new Error("File not found");
          }
          return this.getSafeURL(blob);
        })
      );
  }

  getRawImage(url: string): Observable<any> {
    return this.authHttp.getImage(url)
      .pipe(
        map((blob: any) => {
          if (!blob.type.startsWith("image/")) {
            throw new Error("File not found");
          }
          return blob;
        })
      );
  }

  getBase64Image(url: string, imageCallback: (image: string) => any): void {
    this.authHttp.getImage(url)
      .pipe(
        map((blob: any) => {
          if (!blob.type.startsWith("image/")) {
            throw new Error("File not found");
          }
          return blob;
        }),
        catchError(() => {
          return of(null);
        })
      ).subscribe((imageBlob) => {
        if (imageBlob) {
          const reader = new FileReader();
          const binaryString = reader.readAsDataURL(imageBlob);
          reader.onload = (event: any) => {
            imageCallback(event.target.result);
          };
        } else {
          imageCallback("");
        }
      });
  }

  /**
   * Get an image with the given ObjectID
   * @param {string} id
   * @returns {Observable<any>}
   */
  getImageById(id: string) {
    return this.getImage(AppConstants.GLOBAL_FILE_BY_ID + "/" + id);
  }

  /**
   * Get a video from target URL
   * @param {string} url
   * @returns {Observable<any>}
   */
  getVideo(url: string): Observable<any> {
    return this.authHttp.getVideo(url)
      .pipe(
        map((blob) => {
          return this.getSafeURL(blob);
        })
      );
  }

  /**
   * Get a video with the given ObjectID
   * @param {string} id
   * @returns {Observable<any>}
   */
  getVideoById(id: string) {
    return this.getVideo(AppConstants.GLOBAL_FILE_BY_ID + "/" + id);
  }

  /**
   * Get a user's avatar
   * @returns {Observable<any>}
   */
  getUserAvatar(userId: string): Observable<any> {
    let url = "";
    if (userId) {
      url = AppConstants.FILE_SERVER + "/" + EFileGroup.AVATAR + "?uploader=" + userId;
    } else {
      url = "assets/img/useravatar/0.png";
    }

    return this.getImage(url)
      .pipe(
        catchError(() => of("assets/img/useravatar/0.png"))
      );
  }

  /**
   * Get a list of files in the given file group and optionally subgroup
   * @param {string} filegroup
   * @param {string?} subgroup
   * @returns {Observable<FileInfo[]>}
   */
  getFileList(filegroup: string = "", subgroup: string = ""): Observable<FileInfo[]> {
    let url = AppConstants.FILE_SERVER;
    if (filegroup) {
      url += "/" + filegroup;

      if (subgroup) {
        url += "/" + subgroup;
      }
    }

    url += AppConstants.GET_FILE_LIST;

    return this.authHttp.get<FileInfo[]>(url).pipe(
      catchError(() => EMPTY)
    );
  }

  /**
   * Get a file count in the given file group and subgroup
   * @param {string} filegroup
   * @param {string?} subgroup
   * @returns {Observable<{ count: number }>}
   */
  getFileCount(filegroup: string = "", subgroup: string = ""): Observable<number> {
    let url = AppConstants.FILE_SERVER;
    if (filegroup) {
      url += "/" + filegroup;

      if (subgroup) {
        url += "/" + subgroup;
      }
    }

    url += AppConstants.GET_FILE_COUNT;

    return this.authHttp.get<{ count: number }>(url).pipe(
      map((result: { count: number }) => result.count),
      catchError(() => of(0))
    );
  }

  /**
   * Get a file by its ObjectID, specify filegroup and subgroup optionally
   * @param {string} objectID
   * @param {string} filegroup
   * @param {string} subgroup
   * @returns {Observable<any>}
   */
  getFileById(objectID: string, filegroup = "", subgroup = ""): Observable<any> {
    let url = AppConstants.FILE_SERVER;
    if (filegroup) {
      url += "/" + filegroup;

      if (subgroup) {
        url += "/" + subgroup;
      }
    }

    url += "/id/" + objectID;

    return this.authHttp.getFile(url).pipe(
      catchError(() => EMPTY)
    );
  }

  /**
   * Get files by their objectIDs and return them in a zip file
   */
  getFilesInZipById(objectIDs: string | string[], filegroup = "", subgroup = ""): Observable<any> {
    let url = AppConstants.FILE_SERVER;
    if (filegroup) {
      url += "/" + filegroup;

      if (subgroup) {
        url += "/" + subgroup;
      }
    }

    url += "/id";

    if (!Array.isArray(objectIDs)) {
      objectIDs = [objectIDs];
    }

    return this.authHttp.getFiles(url, "post", {ids: objectIDs}).pipe(
      catchError(() => EMPTY)
    );
  }

  /**
   * Get all files in a filegroup/subgroup
   */
  getAllFilesInZip(filegroup = "", subgroup = ""): Observable<any> {
    let url = AppConstants.FILE_SERVER;
    if (filegroup) {
      url += "/" + filegroup;

      if (subgroup) {
        url += "/" + subgroup;
      }
    }

    return this.authHttp.getFiles(url, "get").pipe(
      catchError(() => EMPTY)
    );
  }

  /**
   * Query the fileservice for a file
   * @param {HttpParams} query
   * @param {string} filegroup
   * @param {string} subgroup
   * @returns {Observable<any>}
   */
  getFileByQuery(query: HttpParams, filegroup = "", subgroup = ""): Observable<any> {
    let url = AppConstants.FILE_SERVER;
    if (filegroup) {
      url += "/" + filegroup;

      if (subgroup) {
        url += "/" + subgroup;
      }
    }
    return this.authHttp.get(url, query).pipe(
      catchError(() => EMPTY)
    );
  }

  /**
   * Download a file sent in the given response to the local filesystem
   * @param res
   */
  downloadFromGetResponse(res) {
    const contentDisposition = res.headers.get("Content-Disposition");
    const filename = decodeURI(contentDisposition.split("=")[1].replace(/"/g, ""));

    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      // IE/Edge
      window.navigator.msSaveOrOpenBlob(res.body, filename);
    } else {
      // All Other Browsers
      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(res.body);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(a.href);
    }
  }

  /**
   * Get a safe URL from a blob
   * @param blob
   */
  getSafeURL(blob) {
    return this.sanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(blob));
  }

  /**
   * Upload a unique file to the fileservice
   * WARNING: Uploading a unique file deletes every other file uploaded by the current user in the specified filegroup
   * @param {File[]} files
   * @param {object} metadata
   * @param {string} filegroup
   * @param {string} subgroup
   * @param {string[]} uniqueFilter
   * @returns {Observable<any>}
   */
  uploadUniqueFiles(files: File[], metadata: object, filegroup: string, subgroup: string = "", uniqueFilter: string[] = []): Observable<any> {
    const form = new FormData();
    form.append("metadata", JSON.stringify(metadata));
    files.forEach((file) => {
      form.append("file", file);
    });
    if (uniqueFilter.length > 0) {
      uniqueFilter.forEach((f) => form.append("overwrite", f));
    }

    let url = AppConstants.FILE_SERVER + "/" + filegroup;
    if (subgroup) {
      url += "/" + subgroup;
    }

    return this.authHttp.post(url, form, null, "put", "multipart/form-data", true).pipe(
      catchError(() => EMPTY)
    );
  }

  /**
   * Upload one or more files to the fileservice
   * @param {File[]} files
   * @param {object} metadata
   * @param {string} filegroup
   * @param {string} subgroup
   * @param {string} overwriteFilter
   * @returns {Observable<any>}
   */
  uploadFiles(files: File[], metadata: object, filegroup: string, subgroup: string = "", overwriteFilter: string[] = null): Observable<any> {
    // TODO: Refactor
    if (overwriteFilter !== null) {
      return this.uploadUniqueFiles(files, metadata, filegroup, subgroup, overwriteFilter);
    } else {
      // ------

      const form = new FormData();
      form.append("metadata", JSON.stringify(metadata));
      files.forEach((file) => {
        form.append("file", file);
      });

      let url = AppConstants.FILE_SERVER + "/" + filegroup;
      if (subgroup) {
        url += "/" + subgroup;
      }

      return this.authHttp.post(url, form, null, "post", "multipart/form-data", true).pipe(
        catchError(() => EMPTY)
      );
    }
  }

  /**
   * Delete a file from the fileservice
   * @param {string} objectID
   * @param {string} filegroup
   * @param {string} subgroup
   * @returns {Observable<any>}
   */
  deleteFile(objectID: string, filegroup: string, subgroup: string = ""): Observable<any> {
    let url = AppConstants.FILE_SERVER + "/" + filegroup;
    if (subgroup) {
      url += "/" + subgroup;
    }

    url += "/" + objectID;
    return this.authHttp.delete(url, null).pipe(
      catchError(() => EMPTY)
    );
  }

  /**
   * Delete files from the fileservice
   * @param {string[]} objectIDs
   * @param {string} filegroup
   * @param {string} subgroup
   * @returns {Observable<any>}
   */
  deleteFiles(objectIDs: string[], filegroup: string, subgroup: string): Observable<any> {
    let url = AppConstants.FILE_SERVER + "/" + filegroup;
    if (subgroup) {
      url += "/" + subgroup;
    }
    return this.authHttp.delete(url, {file: objectIDs}).pipe(
      catchError(() => EMPTY)
    );
  }

  /**
   * Read a file from JSON
   * @param event
   * @returns {Observable<any>}
   */
  importFromJson(event): Observable<any> {
    return new Observable((observer) => {
      const fileReader = new FileReader();
      fileReader.readAsText(event.target.files[0]);
      fileReader.onload = (ev) => {
        observer.next(JSON.parse((ev.target as any).result));
        observer.complete();
      };
    }).pipe(
      catchError(() => EMPTY)
    );
  }

  getDocumentManagerCategoryOrder(filegroup: string, subgroup: string): Observable<{ result: boolean, order?: ICategoryOrder }> {
    return this.authHttp.get<{ result: boolean, order: ICategoryOrder }>(AppConstants.FILE_SERVER_CATEGORY_ORDER + "/" + filegroup + "/" + subgroup)
      .pipe(
        catchError(() => of({ result: false }))
      );
  }

  updateDocumentManagerCategoryOrder(filegroup: string, subgroup: string, categoryOrder: IOrderedCategory[]) {
    return this.authHttp.post<{ result: boolean }>(AppConstants.FILE_SERVER_CATEGORY_ORDER + "/" + filegroup + "/" + subgroup,
      {
        filegroup,
        subgroup,
        orderedCategories: categoryOrder
      })
      .pipe(
        catchError(() => of({ result: false }))
      );
  }
}
